import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../core/environments/environment';
import {
  GetTemplateByIdResponse,
  SendDeclarationByIDResponse,
} from '../../templates/models/templates.model';
import {
  AnswerUpdateResponse,
  CertificationsByUserResponse,
  CreateAnswerBody,
  CreateAnswerResponse,
  CreateAnswerWithOtherBody,
  CreateRowAnswersResponse,
  CreateRowTableBody,
  DropdownAssigmentsByUserResponse,
  endpoint,
  GetAdvanceFormResponse,
  GetFormResponse,
  FormsValidateLimitDateResponse,
  GetRowTableResponse,
  QuestionTableElement,
  UpdateQuestionTableResponse,
  RequestExtensionPayload,
  UpdateAnswerBody,
  UpdateAnswerResponse,
  UpdateAnswerWithOtherBody,
} from '../models/declarant-section.model';
import { map, Observable, tap } from 'rxjs';
import { Option } from 'declarabid-library';
import { toLocalDateString } from '../../../core/utils/date.utils';
import {
  FormProgressResponse,
  progressBarEndpoint,
} from '../models/form-progress.model';

@Injectable({
  providedIn: 'root',
})
export class DeclarantDashboardService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = environment.apiUrl;

  public activeForm = signal<GetFormResponse>({} as GetFormResponse);

  public get validateFormLimitDateDataValue() {
    return this.validateFormLimitDateData();
  }
  private readonly validateFormLimitDateData =
    signal<FormsValidateLimitDateResponse | null>(null);

  public getDeclarantSectionById(template_id: number) {
    return this._http
      .get<GetTemplateByIdResponse>(
        `${this._apiUrl}/${endpoint.getTemplate}/${template_id}`,
      )
      .pipe(
        map((res) => {
          const data = res?.data as any;
          if (!data) return res;

          const normalizeQuestion = (q: any) => {
            if (!q) return q;
            if (!q.type && q.typeQuestion) q.type = q.typeQuestion;
            return q;
          };

          const normalizeElement = (el: any) => {
            if (!el) return el;
            if (
              el.element_type === 'question' ||
              typeof el.question_id === 'number'
            ) {
              return normalizeQuestion(el);
            }
            if (
              el.element_type === 'question_table' ||
              typeof el.questions_table_id === 'number'
            ) {
              if (!el.type && el.typeTable) el.type = el.typeTable;
              if (Array.isArray(el.elementsTable)) {
                el.elementsTable = el.elementsTable.map((c: any) =>
                  normalizeQuestion(c),
                );
              }
              return el;
            }
            if (
              el.element_type === 'subSection' ||
              typeof el.template_subsection_id === 'number'
            ) {
              if (Array.isArray(el.elements)) {
                el.elements = el.elements.map((q: any) => normalizeQuestion(q));
              }
              return el;
            }
            return el;
          };

          if (Array.isArray(data?.sections)) {
            data.sections = data.sections.map((sec: any) => ({
              ...sec,
              elements: Array.isArray(sec?.elements)
                ? sec.elements.map((el: any) => normalizeElement(el))
                : [],
            }));
          }

          return { ...res, data };
        }),
      );
  }

  public validateFormLimitDate(formId: number) {
    return this._http
      .get<FormsValidateLimitDateResponse>(
        `${this._apiUrl}/${endpoint.formsValidateLimitDate}/${formId}`,
      )
      .pipe(
        tap((response) => {
          this.validateFormLimitDateData.set(response);
        }),
      );
  }

  public getForm(form_id: number, user_id: number, sectionPosition?: number) {
    const url =
      sectionPosition == undefined
        ? `${this._apiUrl}/${endpoint.getForm}/${form_id}/${user_id}`
        : `${this._apiUrl}/${endpoint.getForm}/${form_id}/${user_id}/${sectionPosition}`;

    return this._http.get<GetFormResponse>(url).pipe(
      map((response) => {
        const normalized = this.normalizeFormResponseForTables(response);
        this.activeForm.set(normalized);
        return normalized;
      }),
    );
  }

  /**
   * Normaliza la respuesta del formulario solo para elementos tipo tabla:
   * asegura que cada answer tenga `answersTable` (por si la API envía `answers_table`).
   */
  private normalizeFormResponseForTables(
    response: GetFormResponse,
  ): GetFormResponse {
    const data = response?.data;
    if (!data?.template?.sections) return response;

    const sections = data.template.sections.map((sec: any) => ({
      ...sec,
      elements: Array.isArray(sec.elements)
        ? sec.elements.map((el: any) => this.normalizeTableElementAnswers(el))
        : sec.elements,
    }));

    return {
      ...response,
      data: {
        ...data,
        template: {
          ...data.template,
          sections,
        },
      },
    };
  }

  private normalizeTableElementAnswers(el: any): any {
    if (
      el?.element_type !== 'question_table' &&
      typeof el?.questions_table_id !== 'number'
    ) {
      return el;
    }
    if (!Array.isArray(el.elementsTable)) return el;

    const elementsTable = el.elementsTable.map((col: any) => {
      const answers = Array.isArray(col.answers)
        ? col.answers.map((a: any) => ({
            ...a,
            answersTable: a.answersTable ?? a.answers_table ?? [],
          }))
        : col.answers;
      return { ...col, answers };
    });

    return { ...el, elementsTable };
  }

  public postCreateAnswer(
    requestBody: CreateAnswerBody | CreateAnswerWithOtherBody,
    idForm: number,
  ) {
    const resolvedSectionPosition =
      requestBody.sectionPosition ?? this.getCurrentTemplateSectionPosition();
    const body = this.withSectionPosition(
      {
        ...requestBody,
        idForm,
      },
      resolvedSectionPosition,
    );
    return this._http.post<CreateAnswerResponse>(
      `${this._apiUrl}/${endpoint.createAnswer}`,
      body,
    );
  }

  public sendDeclaration(form_id: number, idReviewer?: number) {
    const url = idReviewer
      ? `${this._apiUrl}/${endpoint.sendDeclaration}/${form_id}/${idReviewer}`
      : `${this._apiUrl}/${endpoint.sendDeclaration}/${form_id}`;

    return this._http.put<SendDeclarationByIDResponse>(url, null);
  }

  public updateAnswer(
    answer_id: number,
    requestBody: UpdateAnswerBody | UpdateAnswerWithOtherBody,
    idForm: number,
  ) {
    const resolvedSectionPosition =
      requestBody.sectionPosition ?? this.getCurrentTemplateSectionPosition();
    const body = this.withSectionPosition(
      {
        ...requestBody,
        idForm,
      },
      resolvedSectionPosition,
    );
    return this._http.put<UpdateAnswerResponse>(
      `${this._apiUrl}/${endpoint.updateAnswer}/${answer_id}`,
      body,
    );
  }

  public deleteAnswer(answer_id: number, sectionPosition?: number) {
    const resolvedSectionPosition =
      sectionPosition ?? this.getCurrentTemplateSectionPosition();
    const params = this.buildSectionParams(resolvedSectionPosition);
    return this._http.delete<any>(
      `${this._apiUrl}/${endpoint.deleteAnswer}/${answer_id}`,
      { params },
    );
  }

  private withSectionPosition<T extends { sectionPosition?: number }>(
    body: T,
    sectionPosition: number | null | undefined,
  ): T {
    if (sectionPosition == null) return body;
    return { ...body, sectionPosition };
  }

  private buildSectionParams(
    sectionPosition: number | null | undefined,
  ): HttpParams | undefined {
    if (sectionPosition == null) return undefined;
    return new HttpParams().set('sectionPosition', String(sectionPosition));
  }

  private getCurrentTemplateSectionPosition(): number | null {
    const sections = this.activeForm()?.data?.template?.sections;
    if (!Array.isArray(sections) || sections.length === 0) return null;
    const value = Number(sections[0]?.template_section_position);
    return Number.isNaN(value) ? null : value;
  }

  public dropdownAssigmentsById(): Observable<Option[]> {
    return this._http
      .get<DropdownAssigmentsByUserResponse>(
        `${this._apiUrl}/${endpoint.dropdownAssigmentsByUser}`,
      )
      .pipe(
        map((response) => {
          return response.data.map((forms) => ({
            viewValue: forms.form_name,
            value: forms.form_id.toString(),
          }));
        }),
      );
  }

  public updateFormStatus(form_id: number) {
    return this._http.put<any>(
      `${this._apiUrl}/${endpoint.updateStatusForm}/${form_id}`,
      null,
    );
  }

  public getAdvanceForm(form_id: number) {
    return this._http.get<GetAdvanceFormResponse>(
      `${this._apiUrl}/${endpoint.getAdvanceForm}/${form_id}`,
    );
  }

  public bulkAnswerQuestionTable(
    idQuestionTable: number,
    bulkAnserBody: any[],
  ) {
    return this._http.post<any>(
      `${this._apiUrl}/${endpoint.bulkAnswerQuestionTable}/${idQuestionTable}`,
      bulkAnserBody,
    );
  }

  public bulkAnswerUpdateQuestionTable(bulkAnserBody: any[]) {
    return this._http.put<any>(
      `${this._apiUrl}/${endpoint.bulkAnswerUpdateQuestionTable}`,
      bulkAnserBody,
    );
  }

  public getRowTable(tableId: number, rowNumber: number) {
    return this._http.get<GetRowTableResponse>(
      `${this._apiUrl}/${endpoint.getRowTable}/${tableId}/${rowNumber}`,
    );
  }

  public uploadFile(
    container: string,
    files: File[],
    idQuestion: number,
    idForm: number,
    document_description: string,
    answer_id: number | null = null,
  ) {
    const formData = new FormData();
    // El backend espera el campo "file" (singular) para cada archivo
    for (const file of files) {
      formData.append('file', file, file.name);
    }
    formData.append('idQuestion', idQuestion.toString());
    // Día civil local (FormData); sin ISO completo.
    formData.append('createDate', toLocalDateString(new Date()));
    formData.append('idForm', idForm.toString());
    formData.append('document_description', document_description);
    if (answer_id !== undefined && answer_id !== null) {
      formData.append('idAnswer', answer_id.toString());
    }

    return this._http.post<any>(
      `${this._apiUrl}/${endpoint.uploadFile}/${container}`,
      formData,
    );
  }

  public uploadFileQuestion(
    files: File[],
    idQuestion: number,
    idForm: number,
    document_description: string,
    answer_id: number | null = null,
  ) {
    const formData = new FormData();
    // El backend espera el campo "file" (singular) para cada archivo
    for (const file of files) {
      formData.append('file', file, file.name);
    }
    formData.append('idQuestion', idQuestion.toString());
    formData.append('idForm', idForm.toString());
    formData.append('document_description', document_description);
    if (answer_id !== undefined && answer_id !== null) {
      formData.append('idAnswer', answer_id.toString());
    }

    return this._http.post<any>(
      `${this._apiUrl}/${endpoint.uploadFilesQuestion}`,
      formData,
    );
  }

  public getDocument(
    form_id: number,
    question_id: number,
    answer_id: number | null = null,
  ) {
    return this._http.get<any>(
      `${this._apiUrl}/${endpoint.getDocument}/${question_id}/${form_id}/${answer_id}`,
    );
  }

  public deleteFile(container: string, idGenericsUrl: number) {
    const encContainer = encodeURIComponent(container);
    return this._http.delete<any>(
      `${this._apiUrl}/${endpoint.deleteFile}/${encContainer}/${idGenericsUrl}`,
    );
  }

  public deleterowTable(
    idQuestionTable: number,
    rowNumber: number,
    idForm: number,
  ) {
    return this._http.delete<any>(
      `${this._apiUrl}/${endpoint.deleteRowTable}/${idQuestionTable}/${rowNumber}?idForm=${idForm}`,
    );
  }

  public createRowTable(createRowTableBody: CreateRowTableBody) {
    return this._http.post<CreateRowAnswersResponse>(
      `${this._apiUrl}/${endpoint.createRowTable}`,
      createRowTableBody,
    );
  }

  public updateAnswersTableRow(
    idAnswer: number,
    idForm: number,
    idQuestion: number,
    requestBody: CreateAnswerBody,
  ) {
    return this._http.put<AnswerUpdateResponse>(
      `${this._apiUrl}/${endpoint.updateAnswersTableRow}/${idAnswer}?idForm=${idForm}&idQuestion=${idQuestion}`,
      requestBody,
    );
  }

  public updateTable(idForm: number, idQuestionTable: number) {
    return this._http
      .get<UpdateQuestionTableResponse>(
        `${this._apiUrl}/${endpoint.updateTable}/${idQuestionTable}/${idForm}`,
      )
      .pipe(
        map((response) => {
          const table =
            this._extractQuestionTableFromUpdateResponse(
              response,
              idQuestionTable,
            ) ?? response?.data;

          const rawElements = this._getTableColumns(table);

          if (!table || !rawElements) {
            return response;
          }

          const normalizedElements = rawElements.map((col: any) => {
            const answers = Array.isArray(col?.answers)
              ? col.answers.map((a: any) => ({
                  ...a,
                  answersTable: a.answersTable ?? a.answers_table ?? [],
                }))
              : [];
            return { ...col, answers };
          });

          return {
            ...response,
            data: {
              ...table,
              elementsTable: normalizedElements,
            },
          };
        }),
      );
  }

  private _extractQuestionTableFromUpdateResponse(
    rawResponse: { data?: unknown } | null | undefined,
    idQuestionTable: number,
  ): QuestionTableElement | null {
    const data = rawResponse?.data as Record<string, unknown> | undefined;
    if (!data) return null;

    // Caso A: el endpoint devuelve directamente la tabla en `data`.
    const directTable = data as Partial<QuestionTableElement>;
    const directColumns = this._getTableColumns(data);
    if (
      typeof directTable?.questions_table_id === 'number' &&
      Array.isArray(directColumns)
    ) {
      return {
        ...directTable,
        elementsTable: directColumns,
      } as QuestionTableElement;
    }

    // Caso B: el endpoint devuelve formulario completo en `data.template.sections`.
    const template = data?.['template'] as Record<string, unknown> | undefined;
    const sections = template?.['sections'];
    if (!Array.isArray(sections)) return null;

    for (const section of sections) {
      const elements = (section as { elements?: unknown[] })?.elements;
      if (!Array.isArray(elements)) continue;
      const table = elements.find(
        (el) =>
          typeof el === 'object' &&
          el !== null &&
          'element_type' in el &&
          'questions_table_id' in el &&
          el?.element_type === 'question_table' &&
          Number(
            (el as { questions_table_id?: unknown }).questions_table_id,
          ) === Number(idQuestionTable),
      );
      if (!table) continue;

      const foundTable = table as Record<string, unknown>;
      const elementsTable = this._getTableColumns(foundTable);

      if (!elementsTable) continue;

      const normalizedTable = foundTable as unknown as QuestionTableElement;
      return {
        ...normalizedTable,
        elementsTable,
      } as QuestionTableElement;
    }

    return null;
  }

  private _getTableColumns(
    table:
      | { elementsTable?: unknown[]; questions?: unknown[] }
      | null
      | undefined,
  ): any[] | null {
    if (Array.isArray(table?.elementsTable)) {
      return table.elementsTable as any[];
    }
    if (Array.isArray(table?.questions)) {
      return table.questions as any[];
    }
    return null;
  }

  public getCertificationsById(userId: number, formId: number) {
    const url = `${this._apiUrl}/${endpoint.getCertificates}/${userId}`;

    const params = new HttpParams().set('formId', String(formId));

    return this._http.get<CertificationsByUserResponse>(url, { params });
  }

  public requestExtension(payload: RequestExtensionPayload) {
    const formData = new FormData();
    payload.file.forEach((file) => {
      formData.append('file', file, file.name);
    });

    formData.append('formId', payload.idForm.toString());
    formData.append('extensionUserId', payload.extensionUserId.toString());
    formData.append('reasonId', payload.extensionReasonId.toString());
    formData.append('detailReason', payload.document_description);
    // Solo día civil (el diálogo envía "YYYY-MM-DD"). Evitar new Date("YYYY-MM-DD")
    // porque el motor lo interpreta como medianoche UTC y desfasa el día en UTC−5, etc.
    formData.append('extensionDate', payload.extensionDate.trim());

    const url = `${this._apiUrl}/${endpoint.requestExtension}/extensionsrolereviewer`;
    return this._http.post<{ message: string }>(url, formData);
  }

  public getFormProgress(idForm: number, idUser: number) {
    return this._http.get<FormProgressResponse>(
      `${this._apiUrl}/${progressBarEndpoint.formsProgress}/${idForm}/${idUser}`,
    );
  }
}
